/* PDP layering upsell — accordion, threshold filter, random grid, bundle pricing */

(function () {
  'use strict';

  if (window.customElements && customElements.get('layering-upsell')) return;

  function safeParseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '[]');
    } catch (e) {
      return null;
    }
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.theme_money_format) {
      return Shopify.formatMoney(cents, window.theme_money_format);
    }
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.moneyFormat) {
      return Shopify.formatMoney(cents, window.moneyFormat);
    }
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  function normalizeKey(str) {
    return String(str || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function shuffleArray(arr) {
    const list = arr.slice();
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = list[i];
      list[i] = list[j];
      list[j] = tmp;
    }
    return list;
  }

  function getBaseSizeKey(variant) {
    if (!variant) return '';
    return normalizeKey(variant.option1 || variant.title || '');
  }

  function isVariantInventoryTracked(variant) {
    return variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue';
  }

  function getSortInventory(variant) {
    if (!variant) return -1;
    return Math.max(0, Number(variant.inventory_quantity) || 0);
  }

  function variantMeetsThreshold(variant, threshold) {
    if (!variant || !variant.available) return false;
    if (!isVariantInventoryTracked(variant)) return true;
    return getSortInventory(variant) > threshold;
  }

  function variantMeetsThresholdForSoldAs(variant, threshold, productMeta, baseContext) {
    if (!variant || !variant.available) return false;
    if (!isVariantInventoryTracked(variant)) return true;

    const qty = getSortInventory(variant);
    if (qty <= threshold) return false;

    const pairPurchase = isPairPurchaseForProduct(productMeta, baseContext);
    const isCurtainPairType = !!productMeta.is_curtain_pair_type;
    if (pairPurchase && !isCurtainPairType && qty < 2) return false;

    return true;
  }

  function variantMatchesSize(variant, sizeKey) {
    if (!variant || !sizeKey) return false;
    const keys = [variant.option1, variant.option2, variant.option3, variant.title]
      .map(normalizeKey)
      .filter(Boolean);
    return keys.indexOf(sizeKey) !== -1;
  }

  function matchVariantBySize(product, sizeKey) {
    if (!product || !sizeKey) return null;
    const variants = product.variants || [];
    for (let i = 0; i < variants.length; i++) {
      if (variantMatchesSize(variants[i], sizeKey)) {
        return variants[i];
      }
    }
    return null;
  }

  function productHasAllBaseSizes(product, baseSizeKeys, threshold) {
    if (!baseSizeKeys.length) return true;
    return baseSizeKeys.every(function (sizeKey) {
      const variant = matchVariantBySize(product, sizeKey);
      return variantMeetsThreshold(variant, threshold);
    });
  }

  function getRankedEntry(product, sizeKey, threshold, baseContext) {
    if (!sizeKey) return null;

    const matched = matchVariantBySize(product, sizeKey);
    if (!variantMeetsThresholdForSoldAs(matched, threshold, product, baseContext)) return null;

    return {
      product: product,
      variant: matched,
    };
  }

  function getSoldAsSelectedValue() {
    const hidden = document.getElementById('SoldAsHiddenInput');
    if (hidden && hidden.value) return String(hidden.value).trim();

    const selected = document.querySelector('input[name="properties[Sold As]"]:checked');
    if (selected) return String(selected.value || '').trim();

    const label = document.querySelector('.sold-as-item-code');
    if (label) {
      return String(label.getAttribute('data-selected-value') || label.textContent || '').trim();
    }

    return '';
  }

  function getSoldAsModeRaw(productMeta) {
    if (!productMeta) return '';
    return String(productMeta.sold_as_mode || '').toLowerCase().trim();
  }

  /**
   * Resolved sold-as mode for pricing/display.
   * - single / pair / both: use metafield
   * - Curtain Pair type → pair
   * - empty metafield → single (matches PDP main-product.liquid + card-product.liquid)
   */
  function getSoldAsMode(productMeta) {
    if (!productMeta) return 'single';
    const raw = getSoldAsModeRaw(productMeta);
    if (raw === 'single' || raw === 'pair' || raw === 'both') return raw;
    if (productMeta.is_curtain_pair_type) return 'pair';
    return 'single';
  }

  function hasValidSoldAsMode(productMeta) {
    if (!productMeta) return false;
    if (productMeta.is_curtain_pair_type) return true;
    const raw = getSoldAsModeRaw(productMeta);
    if (!raw) return true;
    return raw === 'single' || raw === 'pair' || raw === 'both';
  }

  function isSoldAsCompatibleWithBase(productMeta, baseContext) {
    return isPairPurchaseForProduct(productMeta, baseContext) === isPairPurchase(baseContext);
  }

  function isPairPurchase(baseContext) {
    const mode = getSoldAsMode(baseContext) || String(window.soldAsMode || 'single').toLowerCase();
    if (baseContext && baseContext.is_curtain_pair_type) return true;
    if (mode === 'pair') return true;
    if (mode === 'both') {
      return getSoldAsSelectedValue().toLowerCase().includes('pair');
    }
    return false;
  }

  /**
   * Resolve pair vs single for a product using its sold_as_mode metafield:
   * - pair: always pair
   * - single: always single
   * - both: mirror base PDP Sold As selection
   * - empty metafield: single only (same as PDP — no pair option)
   */
  function isPairPurchaseForProduct(productMeta, baseContext) {
    const mode = getSoldAsMode(productMeta);

    if (mode === 'pair') return true;
    if (mode === 'single') return false;
    if (mode === 'both') return isPairPurchase(baseContext);

    return false;
  }

  function computeSoldAsPriceCents(variant, productMeta, pairPurchase, mirrorBasePair) {
    if (!variant) return { sale: 0, compare: 0 };

    const basePrice = Number(variant.price) || 0;
    const baseCompare = Number(variant.compare_at_price) || 0;
    const isCurtainPairType = !!productMeta.is_curtain_pair_type;
    const pairDiscount = Number(productMeta.pair_discount_percentage) || 0;

    let sale = basePrice;
    let compare = baseCompare > 0 ? baseCompare : basePrice;

    const usePair = mirrorBasePair ? pairPurchase : false;

    if (usePair) {
      if (!isCurtainPairType) {
        sale = basePrice * 2;
        compare = basePrice * 2;
      }
      if (pairDiscount > 0 && !isCurtainPairType) {
        sale = sale - (sale * pairDiscount) / 100;
      }
    } else {
      sale = basePrice;
      compare = baseCompare > 0 ? baseCompare : 0;
    }

    return { sale: Math.round(sale), compare: Math.round(compare) };
  }

  function getDiscountPercent(sale, compare) {
    if (!compare || compare <= sale) return 0;
    return Math.round(((compare - sale) / compare) * 100);
  }

  function buildPriceCellHtml(label, pricing, cellClass, isActive) {
    const activeClass = isActive ? ' is-active' : '';
    let compareHtml = '';
    if (pricing.compare > pricing.sale) {
      compareHtml = '<s class="price-item price-item--regular">' + formatMoney(pricing.compare) + '</s>';
    }
    const pct = getDiscountPercent(pricing.sale, pricing.compare);
    let discountHtml = '';
    if (pct > 0) {
      discountHtml = '<span class="sale-percentage">' + pct + '% OFF</span>';
    }
    return (
      '<div class="layering-upsell__price-cell ' +
      cellClass +
      activeClass +
      '">' +
      '<span class="layering-upsell__price-label">' +
      label +
      ':' +
      '</span>' +
      '<div class="layering-upsell__price-values">' +
      '<span class="price-item price-item--sale price-item--last">' +
      formatMoney(pricing.sale) +
      '</span>' +
      compareHtml +
      '</div>' +
      discountHtml +
      '</div>'
    );
  }

  function buildCardPriceHtml(variant, productMeta, context) {
    const showPair = isPairPurchaseForProduct(productMeta, context);
    const singleLabel = context.single_label || 'Single Panel';
    const pairLabel = context.pair_label || 'Pair (2 Panels):';
    const pricing = computeSoldAsPriceCents(variant, productMeta, showPair, true);
    const label = showPair ? pairLabel : singleLabel;
    const cellClass = showPair
      ? 'layering-upsell__price-cell--pair'
      : 'layering-upsell__price-cell--single';

    return (
      '<div class="layering-upsell__price-table layering-upsell__price-table--single">' +
      buildPriceCellHtml(label, pricing, cellClass, true) +
      '</div>'
    );
  }

  function getPanelCount(pairPurchase, isCurtainPairType) {
    if (isCurtainPairType) return 2;
    return pairPurchase ? 2 : 1;
  }

  function pluralize(word, count) {
    return count === 1 ? word : word + 's';
  }

  function getCurrentVariant() {
    const el = document.querySelector('variant-selects [data-selected-variant]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function getProductMediaUrls(product, variant) {
    const media = Array.isArray(product.media) ? product.media.filter(Boolean) : [];
    if (media.length) return media;
    if (variant && variant.featured_image) return [variant.featured_image];
    if (product.featured_image) return [product.featured_image];
    return [];
  }

  function buildCardSlider(sliderEl, mediaUrls, altText) {
    if (!sliderEl) return;

    const slidesContainer = sliderEl.querySelector('[data-layering-card-slides]');
    const nav = sliderEl.querySelector('.card__media-slider-nav');
    const dots = sliderEl.querySelector('.card__media-slider-dots');
    if (!slidesContainer) return;

    slidesContainer.innerHTML = '';

    if (!mediaUrls.length) {
      sliderEl.classList.add('layering-upsell__card-slider--empty');
      if (nav) nav.style.display = 'none';
      if (dots) dots.style.display = 'none';
      return;
    }

    mediaUrls.forEach(function (url, index) {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.innerHTML =
        '<img src="' +
        escapeHtml(url) +
        '" alt="' +
        escapeHtml(altText) +
        '" class="layering-upsell__card-image" width="300" height="300" loading="' +
        (index === 0 ? 'eager' : 'lazy') +
        '">';
      slidesContainer.appendChild(slide);
    });

    const hasMultiple = mediaUrls.length > 1;
    sliderEl.classList.toggle('layering-upsell__card-slider--single', !hasMultiple);
    if (nav) nav.style.display = hasMultiple ? '' : 'none';
    if (dots) dots.style.display = hasMultiple ? '' : 'none';
  }

  function initLayeringCardSwipers(root) {
    if (typeof Swiper === 'undefined') {
      setTimeout(function () {
        initLayeringCardSwipers(root);
      }, 50);
      return;
    }

    (root || document).querySelectorAll('[data-layering-card-slider]').forEach(function (slider) {
      if (slider.dataset.swiperInitialized === 'true') return;
      if (slider.classList.contains('layering-upsell__card-slider--single')) return;
      if (slider.classList.contains('layering-upsell__card-slider--empty')) return;

      const slides = slider.querySelectorAll('.swiper-slide');
      if (slides.length < 2) return;

      const nextEl = slider.querySelector('.card__media-slider-next');
      const prevEl = slider.querySelector('.card__media-slider-prev');
      const paginationEl = slider.querySelector('.card__media-slider-dots');

      new Swiper(slider, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        preventClicks: false,
        preventClicksPropagation: false,
        navigation: {
          nextEl: nextEl,
          prevEl: prevEl,
        },
        pagination: {
          el: paginationEl,
          clickable: true,
          bulletClass: 'card__media-slider-dot',
          bulletActiveClass: 'active',
          renderBullet: function (index, className) {
            const label = 'Go to image ' + (index + 1);
            return (
              '<button class="' +
              className +
              '" data-slide="' +
              index +
              '" aria-label="' +
              label +
              '" type="button"><span>' +
              label +
              '</span></button>'
            );
          },
        },
        allowTouchMove: true,
        breakpoints: {
          1025: {
            allowTouchMove: false,
          },
        },
      });

      slider.dataset.swiperInitialized = 'true';
    });
  }

  class LayeringUpsell extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.context = {};
      this.selectedHandle = '';
      this.displayProducts = null;
      this.desktopLimit = 3;
      this.mobileLimit = 4;
      this.inventoryThreshold = 0;
      this._mediaQuery = null;
      this._lastSizeKey = '';
      this._lastSoldAs = '';
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.products = safeParseJSON(this.querySelector('[data-layering-products]')) || [];
      this.context = safeParseJSON(this.querySelector('[data-layering-context]')) || {};
      this.desktopLimit = parseInt(this.dataset.desktopLimit || this.context.desktop_product_limit || '3', 10);
      this.mobileLimit = parseInt(this.dataset.mobileLimit || this.context.mobile_product_limit || '4', 10);
      this.inventoryThreshold = parseInt(
        this.dataset.inventoryThreshold || this.context.inventory_threshold || '0',
        10
      );

      this.details = this.querySelector('[data-layering-upsell-details]');
      this.body = this.querySelector('[data-layering-upsell-body]');
      this.grid = this.querySelector('[data-layering-grid]');
      this.cardTemplate = this.querySelector('[data-layering-card-template]');
      this.headingClosed = this.querySelector('[data-layering-heading-closed]');
      this.headingOpen = this.querySelector('[data-layering-heading-open]');
      this.noSheersInput = this.querySelector('[data-layering-no-sheers]');

      this.bindEvents();
      this.syncAccordionHeadings();
      this.renderGrid();

      this._mediaQuery = window.matchMedia('(max-width: 768px)');
      if (this._mediaQuery.addEventListener) {
        this._mediaQuery.addEventListener('change', () => this.onViewportChange());
      } else if (this._mediaQuery.addListener) {
        this._mediaQuery.addListener(() => this.onViewportChange());
      }
    }

    bindEvents() {
      if (this.details) {
        this.details.addEventListener('toggle', () => this.syncAccordionHeadings());
      }

      this.addEventListener('change', (e) => {
        if (e.target && e.target.name === 'layering-upsell-product') {
          this.onSelectionChange(e.target);
        }
      });

      document.addEventListener('change', (e) => {
        if (e.target && e.target.name === 'properties[Sold As]') {
          this.onSoldAsChange();
        }
      });

      document.addEventListener('click', (e) => {
        if (e.target && e.target.closest && e.target.closest('.soldas-btn')) {
          setTimeout(() => this.onSoldAsChange(), 60);
        }
      });

      const sectionId = this.context.section_id;
      if (sectionId) {
        const qtyInput = document.querySelector('#Quantity-' + sectionId);
        if (qtyInput) {
          qtyInput.addEventListener('input', () => this.updateBundlePrice());
          qtyInput.addEventListener('change', () => this.updateBundlePrice());
        }
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, () => {
          this.onBaseSelectionChange();
        });
      }

      if (!getCurrentVariant()) {
        setTimeout(() => {
          this.onBaseSelectionChange();
        }, 300);
      }
    }

    onBaseSelectionChange() {
      this.displayProducts = null;
      this.renderGrid({ keepSelection: true });
      this.updateBundlePrice();
    }

    onSoldAsChange() {
      clearTimeout(this._soldAsTimer);
      this._soldAsTimer = setTimeout(() => {
        this.displayProducts = null;
        this.renderGrid({ keepSelection: true });
        this.updateBundlePrice();
      }, 50);
    }

    onViewportChange() {
      this.displayProducts = null;
      this.renderGrid({ keepSelection: true });
    }

    syncAccordionHeadings() {
      const isOpen = this.details ? this.details.open : false;
      if (this.headingClosed) this.headingClosed.hidden = isOpen;
      if (this.headingOpen) this.headingOpen.hidden = !isOpen;
    }

    getLimitForViewport() {
      if (this._mediaQuery && this._mediaQuery.matches) {
        return this.mobileLimit;
      }
      return this.desktopLimit;
    }

    getBaseProductJson() {
      const id = this.context.base_product_id;
      if (!id) return null;
      return safeParseJSON(document.getElementById('ProductJson-' + id));
    }

    getBaseSizeKeys() {
      const data = this.getBaseProductJson();
      if (!data || !data.variants) return [];

      const keys = [];
      const seen = {};

      data.variants.forEach(function (variant) {
        if (!variant || !variant.available) return;
        const key = getBaseSizeKey(variant);
        if (key && !seen[key]) {
          seen[key] = true;
          keys.push(key);
        }
      });

      return keys;
    }

    getEligibleProducts(sizeKey) {
      const threshold = this.inventoryThreshold;
      const baseContext = this.context;

      if (!sizeKey) return [];

      return this.products.filter(function (product) {
        if (!hasValidSoldAsMode(product)) return false;
        if (!isSoldAsCompatibleWithBase(product, baseContext)) return false;
        const variant = matchVariantBySize(product, sizeKey);
        return variantMeetsThresholdForSoldAs(variant, threshold, product, baseContext);
      });
    }

    pickDisplayProducts() {
      const variant = getCurrentVariant();
      const sizeKey = getBaseSizeKey(variant);
      const eligible = this.getEligibleProducts(sizeKey);
      const limit = this.getLimitForViewport();

      if (!sizeKey || !eligible.length) {
        return [];
      }

      const shuffled = shuffleArray(eligible);
      const selected = [];

      for (let i = 0; i < shuffled.length && selected.length < limit; i++) {
        const entry = getRankedEntry(shuffled[i], sizeKey, this.inventoryThreshold, this.context);
        if (entry) selected.push(entry);
      }

      return selected;
    }

    hideWidget() {
      this.style.display = 'none';
      window.layeringUpsellActive = false;
      if (typeof window.syncSecondaryPrice === 'function') {
        window.syncSecondaryPrice();
      }
    }

    showWidget() {
      this.style.display = '';
    }

    renderGrid(options) {
      options = options || {};
      if (!this.grid || !this.cardTemplate) return;

      const variant = getCurrentVariant();
      const sizeKey = getBaseSizeKey(variant);
      const soldAs = getSoldAsSelectedValue();

      if (sizeKey !== this._lastSizeKey || soldAs !== this._lastSoldAs) {
        this.displayProducts = null;
        this._lastSizeKey = sizeKey;
        this._lastSoldAs = soldAs;
      }

      if (!options.keepSelection) {
        this.displayProducts = null;
      }

      if (!this.displayProducts) {
        this.displayProducts = this.pickDisplayProducts();
      }

      if (!this.displayProducts.length) {
        this.hideWidget();
        return;
      }

      this.showWidget();

      const visible = this.displayProducts;
      const displaySize = variant ? String(variant.option1 || variant.title || '').trim() : '';

      const previousHandle = this.selectedHandle;
      const validHandles = visible.map(function (item) {
        return item.product.handle;
      });

      if (previousHandle && validHandles.indexOf(previousHandle) === -1) {
        this.selectedHandle = '';
        if (this.noSheersInput) {
          this.noSheersInput.checked = true;
        }
      }

      this.grid.innerHTML = '';

      visible.forEach((item, index) => {
        const card = this.cardTemplate.content.firstElementChild.cloneNode(true);
        const radio = card.querySelector('input[type="radio"]');
        const badge = card.querySelector('[data-layering-badge]');
        const titleEl = card.querySelector('[data-layering-card-title]');
        const sliderEl = card.querySelector('[data-layering-card-slider]');
        const sizeEl = card.querySelector('[data-layering-card-size]');
        const priceEl = card.querySelector('[data-layering-card-price]');

        const handle = item.product.handle;
        radio.value = handle;

        if (index === this.mobileLimit - 1 && this.mobileLimit > this.desktopLimit) {
          card.classList.add('layering-upsell__card--mobile-only');
        }

        if (index === 0) {
          card.classList.add('is-most-popular');
          if (badge) badge.classList.add('is-visible');
        } else {
          card.classList.remove('is-most-popular');
          if (badge) badge.classList.remove('is-visible');
        }

        if (this.selectedHandle === handle) {
          radio.checked = true;
          card.classList.add('is-selected');
        }

        if (titleEl) titleEl.textContent = item.product.title;

        const mediaUrls = getProductMediaUrls(item.product, item.variant);
        buildCardSlider(sliderEl, mediaUrls, item.product.title);

        if (sizeEl) sizeEl.textContent = displaySize;

        if (priceEl) {
          priceEl.innerHTML = buildCardPriceHtml(item.variant, item.product, this.context);
        }

        card.addEventListener('click', function (e) {
          if (e.target.tagName === 'A') return;
          if (e.target.closest('.card__media-slider-nav, .card__media-slider-dots')) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });

        this.grid.appendChild(card);
      });

      if (this.noSheersInput && !this.selectedHandle) {
        this.noSheersInput.checked = true;
      }

      initLayeringCardSwipers(this);
      this.updateBundlePrice();
    }

    onSelectionChange(input) {
      this.selectedHandle = input.value || '';
      this.querySelectorAll('.layering-upsell__card').forEach(function (card) {
        const radio = card.querySelector('input[type="radio"]');
        card.classList.toggle('is-selected', !!(radio && radio.checked));
      });
      this.updateBundlePrice();
    }

    getSelectedProductEntry() {
      if (!this.selectedHandle) return null;
      const visible = this.displayProducts || [];
      for (let i = 0; i < visible.length; i++) {
        if (visible[i].product.handle === this.selectedHandle) {
          return visible[i];
        }
      }
      return null;
    }

    getSelectedVariantId() {
      const entry = this.getSelectedProductEntry();
      return entry && entry.variant ? String(entry.variant.id) : '';
    }

    getLineItemProperties() {
      const soldAsHidden = document.getElementById('SoldAsHiddenInput');
      const unitsHidden = document.getElementById('UnitsPerHiddenInput');
      const twoPackHidden = document.getElementById('TwoPackHiddenInput');

      const soldAs = soldAsHidden ? soldAsHidden.value : getSoldAsSelectedValue();
      const unitsPer = unitsHidden ? unitsHidden.value : '1';
      const twoPack = twoPackHidden ? twoPackHidden.value : '';

      return {
        'SOLD AS': soldAs,
        '_units_per': unitsPer,
        '_2pack': twoPack,
      };
    }

    updateBundlePrice() {
      const secondaryWrapper = document.querySelector('.data-price-wrapper-secondary');
      if (!secondaryWrapper) return;

      const entry = this.getSelectedProductEntry();
      const stickyAddtocartProductPrice = document.querySelector('.sticky-addtocart__price-clone');
      if (stickyAddtocartProductPrice) {
        stickyAddtocartProductPrice.classList.add('layering-bundle-price-active');
      }

      if (!entry) {
        window.layeringUpsellActive = false;
        if (typeof window.syncSecondaryPrice === 'function') {
          window.syncSecondaryPrice();
        }
        return;
      }

      window.layeringUpsellActive = true;

      const baseVariant = getCurrentVariant();
      if (!baseVariant) return;

      const basePairPurchase = isPairPurchase(this.context);
      const upsellPairPurchase = isPairPurchaseForProduct(entry.product, this.context);
      const sectionId = this.context.section_id;
      const qtyInput = sectionId ? document.querySelector('#Quantity-' + sectionId) : null;
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      const basePricing = computeSoldAsPriceCents(baseVariant, this.context, basePairPurchase, true);
      const upsellPricing = computeSoldAsPriceCents(entry.variant, entry.product, upsellPairPurchase, true);

      const bundleSale = (basePricing.sale + upsellPricing.sale) * quantity;
      const bundleCompare = (basePricing.compare + upsellPricing.compare) * quantity;

      const basePanelCount = getPanelCount(basePairPurchase, this.context.is_curtain_pair_type);
      const upsellPanelCount = getPanelCount(upsellPairPurchase, entry.product.is_curtain_pair_type);
      const blackoutLabel = pluralize('Blackout Panel', basePanelCount);
      const sheerLabel = pluralize('Sheer Panel', upsellPanelCount);
      const displaySize = String(baseVariant.option1 || baseVariant.title || '').trim();

      let discountHtml = '';
      if (bundleCompare > bundleSale) {
        const pct = Math.round(((bundleCompare - bundleSale) / bundleCompare) * 100);
        discountHtml = ' <s>' + formatMoney(bundleCompare) + '</s>';
        if (pct > 0) {
          discountHtml += ' <span class="layering-bundle-price__discount">' + pct + '% OFF</span>';
        }
      }

      const includesLine =
        '(Includes ' +
        basePanelCount +
        ' ' +
        pluralize('blackout panel', basePanelCount) +
        ' & ' +
        upsellPanelCount +
        ' ' +
        pluralize('sheer panel', upsellPanelCount) +
        ')';

      secondaryWrapper.innerHTML =
        '<div class="layering-bundle-price">' +
        '<div class="layering-bundle-price__headline">' +
        'Curtains + Sheers: <span>' +
        formatMoney(bundleSale) +
        '</span>' +
        discountHtml +
        '</div>' +
        '<div class="layering-bundle-price__includes">' +
        includesLine +
        '</div>' +
        '<div class="layering-bundle-price__line">' +
        blackoutLabel +
        ' ' +
        displaySize +
        ': ' +
        '<span class="layering-bundle-price__price">' +
        formatMoney(basePricing.sale * quantity) +
        '</span>' +
        '</div>' +
        '<div class="layering-bundle-price__line">' +
        sheerLabel +
        ' ' +
        displaySize +
        ': ' +
        '<span class="layering-bundle-price__price">' +
        formatMoney(upsellPricing.sale * quantity) +
        '</span>' +
        '</div>' +
        '</div>';
    }
  }

  customElements.define('layering-upsell', LayeringUpsell);

  window.updateLayeringBundlePrice = function () {
    const el = document.querySelector('layering-upsell');
    if (el && typeof el.updateBundlePrice === 'function') {
      el.updateBundlePrice();
    }
  };

  window.refreshLayeringUpsell = function () {
    const el = document.querySelector('layering-upsell');
    if (el && typeof el.onSoldAsChange === 'function') {
      el.onSoldAsChange();
    }
  };
})();
